import { Moment } from 'moment-timezone';

import { ViewMode } from 'model/Calendar';

// Set by whoever links to a page that has its own "Back" button, so that Back still has a
// destination when the page was opened in a new tab or deep-linked. Holds the full relative
// URL of the source page, so its filters survive the round trip.
export const RETURN_TO_PARAM = 'returnTo';

// Only same-origin paths are honoured, so a crafted `returnTo` cannot bounce an admin off to
// another site. A leading-slash test is not enough: the URL parser treats a backslash like a
// slash, so `/\evil.example` resolves to another origin just as `//evil.example` does. Resolve
// the path and compare origins instead of pattern-matching the prefixes.
const RETURN_PATH_BASE = 'https://ops-portal.invalid';

export const isSafeReturnPath = (path?: string | null): path is string => {
  if (!path || !path.startsWith('/')) {
    return false;
  }

  try {
    return new URL(path, RETURN_PATH_BASE).origin === RETURN_PATH_BASE;
  } catch {
    return false;
  }
};

const withReturnTo = (path: string, returnTo?: string | null) =>
  isSafeReturnPath(returnTo)
    ? `${path}${path.includes('?') ? '&' : '?'}${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`
    : path;

// OPSPORTAL ROUTES
export const OP_TEACHER_SEARCH_PATH = '/teachers';
export const OP_STUDENT_SEARCH_PATH = '/students';
export const OP_STUDENT_GROUP_SEARCH_PATH = '/students/group';
export const OP_STUDENT_UNCONFIRMED_LESSON_SEARCH_PATH = '/students/group/unassigned-groups';

export const OP_STUDENT_GROUP_DETAILS_PATH = '/students/group/:groupId';
export const getStudentGroupDetailsPath = (groupId: string) =>
  OP_STUDENT_GROUP_DETAILS_PATH.replace(':groupId', groupId);
export const OP_LESSONS_PATH = '/lessons';
export const OP_UNASSIGNED_LESSONS_PATH = '/lessons/unassigned-lessons';
export const OP_LESSON_SCHEDULING_PATH = '/lessons/lesson-scheduling';
export const OP_GROUP_LESSON_SCHEDULING_PATH = '/lessons/group-lesson-scheduling';
export const OP_CLASSROOM_CREATION_PATH = '/lessons/classroom-creation';
export const OP_STUDENT_LIVE_MONITOR_PATH_MAIN = '/live-monitor';
export const OP_STUDENT_LIVE_MONITOR_PATH_ALL = '/live-monitor/all';
export const OP_STUDENT_LIVE_MONITOR_PATH_EMPTY = '/live-monitor/empty';

export const OP_CALENDAR_EDIT_ROUTE = '/calendar/:teacherId/edit';
export const OP_CALENDAR_ROUTE = '/calendar/:teacherId';
export const getAdminCalendarPath = (teacherId: string, returnTo?: string | null) =>
  withReturnTo(`/calendar/${teacherId}`, returnTo);
export const getAdminCalendarEditPath = (teacherId: string, time: number, returnTo?: string | null) =>
  withReturnTo(`/calendar/${teacherId}/edit?time=${time}`, returnTo);

export const OP_LESSON_HISTORY_CLASS_RECORDS = '/lesson-history';
export const OP_LESSON_HISTORY_CLASS_RECORD_DETAILS = '/lesson-history/:bookingId';
export const getLessonHistoryPath = (bookingId: string = ':bookingId') => `/lesson-history/${bookingId}`;
export const OP_LESSON_HISTORY_INVOICES = '/lesson-history/invoices';
export const OP_LESSON_HISTORY_INVOICE = '/lesson-history/invoices/:invoiceId';
export const getAdminInvoicePath = (invoiceId: string = ':invoiceId') => `/lesson-history/invoices/${invoiceId}`;
export const OP_LESSON_HISTORY_STUDENT_REVIEWS = '/lesson-history/student-reviews';
export const OP_LESSON_HISTORY_CLASS_SUMMARY = '/lesson-history/class-summary';

export const OP_STUDENT_PORTAL_LESSONS = '/student-portal/lessons';
export const OP_STUDENT_PORTAL_LESSONS_DETAILS = '/student-portal/lessons/:bookingId';
export const getAdminStudentPortalLessonPath = (bookingId: string = ':bookingId') =>
  `/student-portal/lessons/${bookingId}`;

export const OP_SCHOOL_SETTINGS = '/school-settings';
export const OP_CREATE_SCHOOL_PATH = '/create-school';
export const OP_SCHOOL_SETTINGS_LESSON_DEFINITIONS = '/school-settings/lesson-definitions';
export const OP_SCHOOL_SETTINGS_ADMINS = '/school-settings/admins';
export const OP_SCHOOL_SETTINGS_BLOCK_LIST = '/school-settings/block-list';
export const OP_SCHOOL_SETTINGS_ANNOUNCEMENTS = '/school-settings/announcements';
export const OP_SCHOOL_SETTINGS_BLOCK_SLOTS = '/school-settings/blocked-slots';
export const OP_SCHOOL_SETTINGS_GENERATE_INVOICES = '/school-settings/generate-invoices';
export const OP_SCHOOL_SETTINGS_EDIT_DISTRIBUTIONS = '/school-settings/distributions';
export const OP_SCHOOL_SETTINGS_EDIT_SKILLS = '/school-settings/skills';

export const OP_CREATE_TEACHER_PATH = '/create-teacher';

export const OP_OFFLINE_PATH = '/offline';
export const OP_OFFLINE_TEACHER_DASHBOARD_PATH = '/offline/dashboard';
export const OP_OFFLINE_TEACHER_CLASS_PATH = '/offline/class/:classId';

export const OP_HOME_PATH = OP_TEACHER_SEARCH_PATH;

// TEACHERFIRST ROUTES
export const TF_DASHBOARD_ROUTE = '/dashboard';

export const TF_CALENDAR_EDIT_ROUTE = '/calendar/edit';
export const TF_CALENDAR_ROUTE = '/calendar';
export const getTeacherCalendarSearch = (selectedDate: Moment, viewMode: ViewMode, time: number) =>
  `?date=${selectedDate.format('YYYY-MM-DD')}&viewMode=${viewMode}&time=${time}`;

export const TF_PROFILE = '/teacher/:teacherId/profile';

export const TF_LESSON_HISTORY_OVERVIEW = '/lesson-history';
export const TF_LESSON_HISTORY_CLASS_RECORDS = '/lesson-history/class-records';
export const TF_LESSON_HISTORY_INVOICES = '/lesson-history/invoices';
export const TF_LESSON_HISTORY_INVOICE = '/lesson-history/invoices/:invoiceId';
export const getTeacherInvoicePath = (invoiceId: string = ':invoiceId') => `/lesson-history/invoices/${invoiceId}`;
export const TF_LESSON_HISTORY_STUDENT_REVIEWS = '/lesson-history/student-reviews';

export const TF_TEACHER_LOBBY_PATH = '/bookings/:bookingId/lobby';
export const getTeacherLobbyPath = (bookingId: string = ':bookingId') => `/bookings/${bookingId}/lobby`;
export const TF_STUDENT_GROUPS_PATH = '/student-groups';
export const TF_STUDENT_GROUPS_DETAILS_PATH = '/student-groups/:groupId';
export const getStudentGroupDetailsTFPath = (groupId: string) =>
  TF_STUDENT_GROUPS_DETAILS_PATH.replace(':groupId', groupId);
export const TF_STUDENT_ASSIGNED_GROUPS_PATH = `${TF_STUDENT_GROUPS_PATH}/assigned-groups`;
export const TF_STUDENT_UNASSIGNED_GROUPS_PATH = `${TF_STUDENT_GROUPS_PATH}/unassigned-groups`;

export const TF_HOME_PATH = TF_DASHBOARD_ROUTE;
export const getClassroomPath = (bookingId: string, participantId: string) =>
  `/bookings/${bookingId}/participants/${participantId}/classroom`;
export const getEvcClassroomPath = (evcClassroomId: string, participantId: string) =>
  `/evc-classrooms/${evcClassroomId}/participants/${participantId}/classroom`;
export const TF_EVC_CLASSROOM = getEvcClassroomPath(':evcClassroomId', ':participantId');

// MISC ROUTES
export const GENERAL_ERROR_PATH = '/error';
export const GENERAL_CLASSROOM = getClassroomPath(':bookingId', ':participantId');
export const UNAUTHORIZED_USER_PATH = '/unauthorized';

export const HOME_PATH = '/';

export const STUDENTS_LOBBY_PATH = '/students-lobby';

// Developer-only mock catalog (gated on isDeveloperMode; hidden from demo customers).
export const MOCK_CATALOG_PATH = '/mock-catalog';

// SHARED ROUTES
export const VERSION_PATH = '/version';
export const SHARED_ACR_PATH = '/acr/:acrId';
export const getAcrPath = (acrId: string = ':acrId') => `/acr/${acrId}`;

export const SHARED_CLASS_PREPARATION_PATH = '/class-preparation/:bookingId';
export const getClassPrepPath = (bookingId: string = ':bookingId') => `/class-preparation/${bookingId}`;

export const OFFLINE_PATH = '/offline';

export const OFFLINE_TEACHER_DASHBOARD_PATH = `${OFFLINE_PATH}/dashboard`;
export const OFFLINE_MY_LESSONS_PATH = `${OFFLINE_PATH}/my-lessons`;
export const OFFLINE_NEW_LESSON_PATH = `${OFFLINE_PATH}/my-lessons/new`;
export const OFFLINE_LESSON_DETAIL_PATH = `${OFFLINE_PATH}/my-lessons/:lessonId`;
export const OFFLINE_TEACHER_CLASS_PATH = `${OFFLINE_PATH}/class/:classId`;
export const OFFLINE_TEACHER_STUDENT_PATH = `${OFFLINE_PATH}/class/:classId/student/:studentId`;
// The class page keeps its active tab in the URL, so going back from a student lands on the same tab.
export const CLASS_TAB_PARAM = 'tab';
export type ClassTab = 'assignments' | 'students';
export const getOfflineClassPath = (classId: string, tab?: 'students') =>
  `${OFFLINE_TEACHER_CLASS_PATH.replace(':classId', encodeURIComponent(classId))}${tab ? `?${CLASS_TAB_PARAM}=${tab}` : ''}`;
// The class tab a student profile was opened from, so its way back returns to that tab. Without
// it, the profile goes back to the Students tab.
export const STUDENT_PROFILE_FROM_PARAM = 'from';
// The assignment a student profile was opened from, so the profile opens that lesson's row.
export const STUDENT_PROFILE_ASSIGNMENT_PARAM = 'assignment';
export const getOfflineStudentProfilePath = (
  classId: string,
  studentId: string,
  from?: ClassTab,
  assignmentId?: string,
) => {
  const params = new URLSearchParams();
  if (from === 'assignments') {
    params.set(STUDENT_PROFILE_FROM_PARAM, 'assignments');
  }
  if (assignmentId) {
    params.set(STUDENT_PROFILE_ASSIGNMENT_PARAM, assignmentId);
  }
  const query = params.toString();
  return `${OFFLINE_TEACHER_STUDENT_PATH.replace(':classId', encodeURIComponent(classId)).replace(
    ':studentId',
    encodeURIComponent(studentId),
  )}${query ? `?${query}` : ''}`;
};
export const OFFLINE_ASSIGNMENT_PREVIEW_PATH = `${OFFLINE_PATH}/assignment/:id`;
export const getOfflineAssignmentPreviewPath = (id: string) => `${OFFLINE_PATH}/assignment/${id}`;
// A coursebook lesson played before it is sent to the class. The lesson plan id alone does
// not name a catalogue lesson, so its course goes in the query.
export const OFFLINE_LESSON_PREVIEW_PATH = `${OFFLINE_TEACHER_CLASS_PATH}/lesson/:lessonPlanId`;
export const LESSON_PREVIEW_COURSE_PARAM = 'courseId';
export const getOfflineLessonPreviewPath = (
  classId: string,
  { courseId, lessonPlanId }: { courseId: string; lessonPlanId: string },
) =>
  `${OFFLINE_LESSON_PREVIEW_PATH.replace(':classId', encodeURIComponent(classId)).replace(
    ':lessonPlanId',
    encodeURIComponent(lessonPlanId),
  )}?${new URLSearchParams({ [LESSON_PREVIEW_COURSE_PARAM]: courseId }).toString()}`;

// Public, PII-redacted read-only view of a shared class. Resolved by share token only -
// the class id is never in the URL. Rendered outside the authenticated app shell.
export const SHARED_CLASS_VIEW_PREFIX = '/shared-classes';
export const SHARED_CLASS_VIEW_PATH = `${SHARED_CLASS_VIEW_PREFIX}/:token`;
export const getSharedClassViewPath = (token: string) => `${SHARED_CLASS_VIEW_PREFIX}/${token}`;
